import {
    createContext,
    ReactNode,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useFrontendAuth } from '../../hooks/useAuth';
import { completeProductTour } from '../../services/api.shop';
import type { ShopUserInfo } from '../../types/auth';
import TourOverlay from './TourOverlay';
import { homeTour } from './tours/homeTour';
import { getMyPetitoSteps, myPetitoTour } from './tours/myPetitoTour';
import type { TourDefinition, TourId, TourStep } from './tours/types';
import {
    isCookieConsentPending,
    normalizeViewportForTour,
    restoreViewportAfterTour,
} from './tourDom';
import { useLoginPrompt } from '../../loginPrompts/loginPromptContext';

export interface StartTourOptions {
    /** When true (default for auto-tour), Done/Skip persists that tour's flag once. */
    markCompleteOnEnd?: boolean;
}

export interface ProductTourContextValue {
    isActive: boolean;
    activeTourId: TourId | null;
    startTour: (id: TourId, options?: StartTourOptions) => void;
    stopTour: (opts?: { markComplete?: boolean }) => void;
    /** Home tour already completed on server. */
    productTourHomeCompleted: boolean;
    /** My Petito tour already completed on server. */
    productTourMyPetitoCompleted: boolean;
}

export const ProductTourContext = createContext<ProductTourContextValue | null>(null);

const TOURS: Record<TourId, TourDefinition> = {
    home: homeTour,
    mypetito: myPetitoTour,
};

function pathTourId(pathname: string): TourId | null {
    const p = (pathname || '/').toLowerCase();
    if (p === '/' || p === '/home') return 'home';
    if (p.startsWith('/my-petito') || p.startsWith('/my-rebaby')) return 'mypetito';
    return null;
}

function isTourCompleted(user: ShopUserInfo | null | undefined, tourId: TourId): boolean {
    if (!user) return true;
    if (tourId === 'home') return !!user.productTourHomeCompleted;
    return !!user.productTourMyPetitoCompleted;
}

function applyTourCompletedOptimistic(
    prev: ShopUserInfo | null | undefined,
    tourId: TourId,
    value: boolean
): ShopUserInfo | null | undefined {
    if (!prev) return prev;
    if (tourId === 'home') return { ...prev, productTourHomeCompleted: value };
    return { ...prev, productTourMyPetitoCompleted: value };
}

export function ProductTourProvider({ children }: { children: ReactNode }) {
    const { user, isAuthenticated, isAuthReady } = useFrontendAuth();
    const {
        grantedId,
        shopOverlayHeld,
        reportWaiting,
        claim,
        releaseClaim,
        settle,
        holdOverlay,
        releaseOverlay,
    } = useLoginPrompt();
    const location = useLocation();
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const [activeTourId, setActiveTourId] = useState<TourId | null>(null);
    const [steps, setSteps] = useState<TourStep[]>([]);
    const [stepIndex, setStepIndex] = useState(0);
    const [markCompleteOnEnd, setMarkCompleteOnEnd] = useState(true);
    const [cookieReady, setCookieReady] = useState(() => !isCookieConsentPending());
    const completingRef = useRef(false);
    const autoStartedForUserRef = useRef<string | null>(null);
    const activeTourIdRef = useRef<TourId | null>(null);
    /** Home tour was opened by the login queue, so finishing it starts the gap. */
    const queueHomeRef = useRef(false);
    /** Ignore stale startTour async after a newer start/stop. */
    const startGenRef = useRef(0);

    useEffect(() => {
        activeTourIdRef.current = activeTourId;
    }, [activeTourId]);

    // Always restore viewport meta if tour tree unmounts mid-tour
    useEffect(() => {
        return () => {
            restoreViewportAfterTour();
        };
    }, []);

    // Same-tab cookie banner accept/reject does not fire storage events — poll briefly.
    useEffect(() => {
        if (cookieReady) return;
        const id = window.setInterval(() => {
            if (!isCookieConsentPending()) setCookieReady(true);
        }, 400);
        return () => window.clearInterval(id);
    }, [cookieReady]);

    const productTourHomeCompleted = !!user?.productTourHomeCompleted;
    const productTourMyPetitoCompleted = !!user?.productTourMyPetitoCompleted;
    const isActive = activeTourId != null && steps.length > 0;

    const persistCompleteOnce = useCallback(
        async (tourId: TourId) => {
            if (!user?.id || completingRef.current) return;
            if (isTourCompleted(user, tourId)) return;
            completingRef.current = true;
            try {
                queryClient.setQueryData<ShopUserInfo | null>(['user'], (prev) =>
                    applyTourCompletedOptimistic(prev, tourId, true) ?? null
                );
                await completeProductTour(tourId);
            } catch (err) {
                console.error('Failed to persist product tour completion', err);
                queryClient.setQueryData<ShopUserInfo | null>(['user'], (prev) =>
                    applyTourCompletedOptimistic(prev, tourId, false) ?? null
                );
            } finally {
                completingRef.current = false;
            }
        },
        [user, queryClient]
    );

    const stopTour = useCallback(
        (opts?: { markComplete?: boolean }) => {
            startGenRef.current += 1; // cancel any in-flight start normalize
            const tourId = activeTourIdRef.current;
            const shouldMark = opts?.markComplete ?? markCompleteOnEnd;
            // Allow auto-start to retry later if we aborted without completing
            if (!shouldMark && tourId && user?.id) {
                const key = `${user.id}:${tourId}`;
                if (autoStartedForUserRef.current === key) {
                    autoStartedForUserRef.current = null;
                }
            }
            setActiveTourId(null);
            setSteps([]);
            setStepIndex(0);
            restoreViewportAfterTour();
            if (shouldMark && tourId) {
                void persistCompleteOnce(tourId);
            }
            if (tourId === 'home' && queueHomeRef.current && shouldMark) {
                queueHomeRef.current = false;
                settle();
            } else if (tourId === 'home' && !shouldMark) {
                queueHomeRef.current = false;
                releaseClaim();
            }
        },
        [markCompleteOnEnd, persistCompleteOnce, releaseClaim, settle, user?.id]
    );

    const startTour = useCallback((id: TourId, options?: StartTourOptions) => {
        const resolved = id === 'mypetito' ? getMyPetitoSteps() : TOURS[id]?.steps ?? [];
        setMarkCompleteOnEnd(options?.markCompleteOnEnd !== false);
        const gen = ++startGenRef.current;
        // Best-effort unzoom first so first measure isn't against a pinch-zoomed viewport.
        // Never scrolls the page to top (see normalizeViewportForTour).
        void (async () => {
            await normalizeViewportForTour();
            if (gen !== startGenRef.current) return;
            setSteps(resolved);
            setStepIndex(0);
            setActiveTourId(id);
        })();
    }, []);

    const handleNext = useCallback(() => {
        setStepIndex((i) => {
            if (i >= steps.length - 1) {
                queueMicrotask(() => stopTour({ markComplete: markCompleteOnEnd }));
                return i;
            }
            return i + 1;
        });
    }, [steps.length, stopTour, markCompleteOnEnd]);

    const handleSkip = useCallback(() => {
        stopTour({ markComplete: markCompleteOnEnd });
    }, [stopTour, markCompleteOnEnd]);

    const handlePrimaryAction = useCallback(
        (step: TourStep) => {
            const href = step.primaryAction?.href;
            stopTour({ markComplete: markCompleteOnEnd });
            if (href) navigate(href);
        },
        [stopTour, markCompleteOnEnd, navigate]
    );

    const homeWaiting =
        isAuthReady &&
        isAuthenticated &&
        !!user?.id &&
        cookieReady &&
        !isCookieConsentPending() &&
        pathTourId(location.pathname) === 'home' &&
        !isTourCompleted(user, 'home');

    useEffect(() => {
        reportWaiting('home-tour', homeWaiting);
    }, [homeWaiting, reportWaiting]);

    useEffect(() => {
        if (!isActive) return;
        holdOverlay('product-tour');
        return () => releaseOverlay('product-tour');
    }, [isActive, holdOverlay, releaseOverlay]);

    // Home tour waits for the login queue. My Petito tour starts on that page, and waits if a login prompt is up.
    useEffect(() => {
        if (!isAuthReady || !isAuthenticated || !user?.id) return;
        if (!cookieReady || isCookieConsentPending()) return;
        if (isActive) return;

        const tourId = pathTourId(location.pathname);
        if (!tourId) return;
        if (isTourCompleted(user, tourId)) return;
        if (tourId === 'home' && grantedId !== 'home-tour') return;
        if (tourId === 'mypetito' && (grantedId != null || shopOverlayHeld)) return;

        const key = `${user.id}:${tourId}`;
        if (autoStartedForUserRef.current === key) return;

        let cancelled = false;
        let attempts = 0;
        const maxAttempts = tourId === 'mypetito' ? 15 : 8;

        const tryStart = () => {
            if (cancelled) return;
            if (isTourCompleted(user, tourId)) return;
            if (isCookieConsentPending()) return;
            if (tourId === 'home' && grantedId !== 'home-tour') return;
            if (tourId === 'mypetito' && (grantedId != null || shopOverlayHeld)) return;

            // Wait until page anchors exist (layout / children query)
            if (tourId === 'mypetito') {
                const ready = document.querySelector(
                    '[data-tour-id="mypetito.addbaby"], [data-tour-id="mypetito.addbaby.empty"], [data-tour-id="mypetito.empty"]'
                );
                if (!ready && attempts < maxAttempts) {
                    attempts += 1;
                    window.setTimeout(tryStart, 200);
                    return;
                }
            } else if (tourId === 'home') {
                const ready = document.querySelector(
                    '[data-tour-id="home.sell"], [data-tour-id="home.sell.mobile"], [data-tour-id="home.journey"], [data-tour-id="home.notifications"]'
                );
                if (!ready && attempts < maxAttempts) {
                    attempts += 1;
                    window.setTimeout(tryStart, 200);
                    return;
                }
            }

            autoStartedForUserRef.current = key;
            if (tourId === 'home') {
                queueHomeRef.current = true;
                claim('home-tour');
            }
            startTour(tourId, { markCompleteOnEnd: true });
        };

        const initialDelay = tourId === 'mypetito' ? 400 : 500;
        const t = window.setTimeout(tryStart, initialDelay);

        return () => {
            cancelled = true;
            window.clearTimeout(t);
        };
    }, [
        isAuthReady,
        isAuthenticated,
        user,
        location.pathname,
        isActive,
        startTour,
        cookieReady,
        grantedId,
        shopOverlayHeld,
        claim,
    ]);

    useEffect(() => {
        if (!user?.id) autoStartedForUserRef.current = null;
    }, [user?.id]);

    const value = useMemo<ProductTourContextValue>(
        () => ({
            isActive,
            activeTourId,
            startTour,
            stopTour,
            productTourHomeCompleted,
            productTourMyPetitoCompleted,
        }),
        [
            isActive,
            activeTourId,
            startTour,
            stopTour,
            productTourHomeCompleted,
            productTourMyPetitoCompleted,
        ]
    );

    return (
        <ProductTourContext.Provider value={value}>
            {children}
            {isActive && (
                <TourOverlay
                    steps={steps}
                    stepIndex={stepIndex}
                    onNext={handleNext}
                    onSkip={handleSkip}
                    onAbort={() => stopTour({ markComplete: false })}
                    onPrimaryAction={handlePrimaryAction}
                />
            )}
        </ProductTourContext.Provider>
    );
}

export default ProductTourProvider;
